/**
 * Connection: XMPP 연결 관리
 *
 * 기존 wehago-conference의 SVConference/Connection.js를 TypeScript로 리팩토링.
 * JitsiConnection 생성, 이벤트 바인딩, 연결/해제 담당.
 */
import type { JitsiConnection } from '@/types/jitsi';
import config from './config';
import { isRecorderMode } from '@/utils/recorderMode';

interface ConnectionHandlers {
  onConnected: (connection: JitsiConnection) => void;
  onFailed: (errorCode: string) => void;
  onDisconnected: (errorCode?: string) => void;
}

class Connection {
  private _connection: JitsiConnection | null = null;
  private _shortJabberId: string = '';

  async connect(
    roomName: string,
    token: string | undefined,
    handlers: ConnectionHandlers
  ): Promise<JitsiConnection> {
    return new Promise((resolve, reject) => {
      const connection = this._createJitsiConnection(roomName, token);
      this._connection = connection;
      this._bindEvents(connection, resolve, reject, handlers);
      connection.connect();
    });
  }

  private _createJitsiConnection(
    roomName: string,
    token: string | undefined
  ): JitsiConnection {
    // WebSocket 우선, 미설정 시 BOSH로 폴백
    const options: Record<string, unknown> = { ...config };
    options.serviceUrl = this._buildServiceUrl(roomName);
    delete options.bosh;
    delete options.websocket;

    // record-ui(jitsi-meet) 앱에서 기본값으로 true를 세팅하던 플래그. 
    // JVB에서 ssrcRewriting을 사용할 경우 이 플래그가 없으면 Unified Plan SDP(m-line 분리) 과정에서 
    // 다수 SSRC가 한 미디어 섹션에 남게 되어 setRemoteDescription 에러가 발생할 수 있습니다.
    options.flags = {
      ...(options.flags as Record<string, unknown> || {}),
      ssrcRewritingEnabled: true
    };

    // Jibri 봇인 경우 URL 해시의 config.iAmRecorder=true 가 유실되지 않도록 명시적으로 주입.
    // 이 값이 없으면 JVB가 Jibri를 일반 참가자로 인식하여, 미디어를 보내지 않는 Jibri를
    // 3분 후 "Idle session timeout"으로 강제 퇴장(종료)시킵니다.
    if (isRecorderMode()) {
      options.iAmRecorder = true;
    }

    return new window.JitsiMeetJS.JitsiConnection(
      undefined, // appId: 기본값 사용
      token,
      options
    );
  }

  private _buildServiceUrl(roomName: string): string {
    const websocketEndpoint = typeof config.websocket === 'string'
      ? config.websocket.trim()
      : '';
    const boshEndpoint = typeof config.bosh === 'string'
      ? config.bosh.trim()
      : '';
    const endpoint = websocketEndpoint || boshEndpoint;

    if (!endpoint) {
      throw new Error('[Connection] Missing websocket/bosh endpoint in conference config.');
    }

    const separator = endpoint.includes('?') ? '&' : '?';

    return `${endpoint}${separator}room=${encodeURIComponent(roomName)}`;
  }

  private _bindEvents(
    connection: JitsiConnection,
    resolve: (c: JitsiConnection) => void,
    reject: (e: string) => void,
    handlers: ConnectionHandlers
  ): void {
    const { events } = window.JitsiMeetJS;

    connection.addEventListener(
      events.connection.CONNECTION_ESTABLISHED,
      () => {
        // Jabber ID에서 shortId 파싱 (방 해시)
        try {
          const jid = connection.getJid();
          this._shortJabberId = jid?.split('@')[0]?.split('-')[0] ?? '';
        } catch {
          this._shortJabberId = '';
        }
        handlers.onConnected(connection);
        resolve(connection);
      }
    );

    connection.addEventListener(
      events.connection.CONNECTION_FAILED,
      (errorCode: unknown) => {
        const code = String(errorCode);
        handlers.onFailed(code);
        reject(code);
      }
    );

    connection.addEventListener(
      events.connection.CONNECTION_DISCONNECTED,
      (message: unknown) => {
        handlers.onDisconnected(message ? String(message) : undefined);
      }
    );
  }

  dispose(): void {
    if (this._connection) {
      try {
        this._connection.disconnect();
      } catch (err) {
        console.warn('[Connection] disconnect error:', err);
      }
      this._connection = null;
    }
  }

  getShortJabberId(): string {
    return this._shortJabberId;
  }

  getConnection(): JitsiConnection | null {
    return this._connection;
  }
}

export default Connection;
